import React, { FunctionComponent, MouseEvent } from 'react';
import {
    Box,
    IconButton,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core';
import Zoom from '@material-ui/core/Zoom';
import { Delete } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import Tooltip from '@material-ui/core/Tooltip';
import gray from '@material-ui/core/colors/grey';
import { ExpenseCard } from '../EditTimeSheet';
import { displayCurrencyAmount } from '../../../../utils/utils';
import InputTextComponent from '../../../../components/InputTextField';
import { ExpenseCategory } from '../../../../store/modules/timeSheet/type';
import AutoCompleteSelect from '../../../../components/AutoCompleteSelect';
import { DropDownField } from '../../../../store/modules/application/types';

type TimeExpenseEntryProps = {
    totalMileageAmount: number;
    readOnly: boolean;
    timeExpenseEntries: ExpenseCard[];
    isExpenseCategoriesLoading: boolean;
    expenseCategories: ExpenseCategory[];
    handleTimeExpenseEntry: Function;
    handleDeleteTimeExpenseRow: Function;
};

const useStyles = makeStyles((theme) => ({
    title: {
        fontSize: 12,
        fontWeight: 600,
        color: '#0277BD',
        padding: 10,
    },
    timeContainer: {
        padding: 25,
        marginTop: 10,
    },
    dialogContent: {
        minHeight: 400,
    },
    tableHeaderCell: {
        fontSize: 14,
        color: '#484848',
        fontWeight: 500,
        padding: 10,
    },
    label: {
        fontSize: 14,
        fontWeight: 500,
        color: theme.palette.text.secondary,

        textAlign: 'right',
        marginRight: theme.spacing(2),
    },
    selectInput: {
        fontSize: 13,
        border: '1px solid rgb(0,0,0,0.23) !important',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        borderRadius: 3,
        marginBottom: '5px',
        color: '#484848 !important',
        minWidth: 150,
    },
    tableRow: {
        verticalAlign: 'top',
        cursor: 'pointer',
        '&:hover ': {
            backgroundColor: '#eeeeee',
            transition: 'ease-in 200ms',
            '& button': {
                opacity: 1,
                transition: 'ease-in 200ms',
            },
        },
    },
    center: {
        justifyContent: 'center',
    },
    height: {
        height: '35px',
    },
    fullWidth: {
        width: '100%',
    },
    editIcon: {
        opacity: 0,
    },
    textField: {
        fontSize: 14,
        fontWeight: 450,
        color: gray[600],
        lineHeight: '21px',
        wordWrap: 'break-word',
        width: '100%',
    },
    textFieldInput: {
        fontSize: 13,
        border: '1px solid rgb(0,0,0,0.23) !important',
        padding: 10,
        borderRadius: 3,
        marginBottom: '5px',
        color: '#484848 !important',
    },
    textFieldAmountInput: {
        fontSize: 13,
        border: '1px solid rgb(0,0,0,0.23) !important',
        padding: 10,
        borderRadius: 3,
        marginBottom: '5px',
        color: '#484848 !important',
        textAlign: 'right',
    },
    mileageTitle: {
        color: '#484848',
        fontSize: '16px',
        lineHeight: '24px',
        paddingBottom: '20px',
    },
    totalTitle: {
        color: '#484848',
        fontSize: '16px',
        lineHeight: '24px',
        textAlign: 'left',
        fontWeight: 600,
    },
    totalAmount: {
        root: {
            '&>.MuiTableCell-root': { borderBottom: '1px solid rgba(224, 224, 224, 1)' },
        },
        color: '#484848',
        fontSize: '16px',
        lineHeight: '24px',
        textAlign: 'right',
    },
    readOnlyLabel: {
        fontSize: '14px',
        color: '#484848;',
        lineHeight: '20px',
    },
}));

const TimeExpenseEntry: FunctionComponent<TimeExpenseEntryProps> = (props) => {
    const {
        totalMileageAmount,
        readOnly,
        expenseCategories,
        timeExpenseEntries,
        handleTimeExpenseEntry,
        handleDeleteTimeExpenseRow,
        isExpenseCategoriesLoading,
    } = props;

    const totalAmount = timeExpenseEntries.reduce((total, item) => total + (item.amount ? +item?.amount! : 0), 0);
    const classes = useStyles();
    const Loader = <Skeleton width="50%" />;
    return (
        <Paper variant="outlined">
            <Typography className={classes.title} component="h1">
                {'Enter Expenses'}
            </Typography>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell className={classes.tableHeaderCell} width="20%">
                                Category
                            </TableCell>
                            <TableCell className={classes.tableHeaderCell} width="20%">
                                Amount
                            </TableCell>
                            <TableCell className={classes.tableHeaderCell} width="60%">
                                Comments
                            </TableCell>

                            <TableCell className={classes.tableHeaderCell}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {timeExpenseEntries.map((expenseEntry, index) => (
                            <TableRow key={expenseEntry.key} className={classes.tableRow}>
                                <TableCell>
                                    {isExpenseCategoriesLoading ? (
                                        Loader
                                    ) : readOnly ? (
                                        <Typography className={classes.readOnlyLabel}>
                                            {
                                                expenseCategories.find(
                                                    (expenseCategory) =>
                                                        expenseCategory.id === expenseEntry?.category.id,
                                                )?.name
                                            }
                                        </Typography>
                                    ) : (
                                        <AutoCompleteSelect
                                            data={expenseCategories.map((expenseCategory: ExpenseCategory) => ({
                                                label: expenseCategory.name,
                                                value: expenseCategory.id,
                                            }))}
                                            onChange={(data: DropDownField | null) => {
                                                handleTimeExpenseEntry(
                                                    expenseEntry.key,
                                                    'expenseCategory',
                                                    data?.value,
                                                );
                                            }}
                                            inputSize={'small'}
                                            value={
                                                expenseEntry?.category?.id
                                                    ? {
                                                          label: expenseEntry.category.name,
                                                          value: expenseEntry.category.id,
                                                      }
                                                    : null
                                            }
                                            variant={'outlined'}
                                            ariaLabel="Expense Category"
                                        />
                                    )}
                                </TableCell>
                                <TableCell>
                                    {readOnly ? (
                                        <Typography className={classes.readOnlyLabel}>
                                            {expenseEntry?.amount
                                                ? displayCurrencyAmount(expenseEntry.amount)
                                                : undefined}
                                        </Typography>
                                    ) : (
                                        <InputTextComponent
                                            id={`amount-${expenseEntry.key}`}
                                            size="small"
                                            fontSize="13"
                                            className={classes.textField}
                                            inputProps={classes.textFieldAmountInput}
                                            onChange={(amount: string) =>
                                                handleTimeExpenseEntry(expenseEntry.key, 'expenseAmount', amount)
                                            }
                                            inputType={'number'}
                                            value={expenseEntry?.amount || ''}
                                        />
                                    )}
                                </TableCell>
                                <TableCell>
                                    {readOnly ? (
                                        <Typography className={classes.readOnlyLabel}>
                                            {expenseEntry?.comments}
                                        </Typography>
                                    ) : (
                                        <InputTextComponent
                                            id={`comment-${expenseEntry.key}`}
                                            size="small"
                                            fontSize="13"
                                            className={classes.textField}
                                            inputProps={classes.textFieldInput}
                                            onChange={(comments: string) =>
                                                handleTimeExpenseEntry(expenseEntry.key, 'expenseComments', comments)
                                            }
                                            value={expenseEntry?.comments}
                                        />
                                    )}
                                </TableCell>

                                <TableCell>
                                    {!readOnly && (
                                        <Box
                                            style={{
                                                display: 'flex',
                                                alignContent: 'center',
                                                alignItems: 'center',
                                            }}
                                        >
                                            {index !== timeExpenseEntries.length - 1 ? (
                                                <Tooltip
                                                    disableFocusListener
                                                    disableTouchListener
                                                    TransitionComponent={Zoom}
                                                    title="Delete"
                                                >
                                                    <IconButton
                                                        onClick={(e: MouseEvent<HTMLElement>) =>
                                                            handleDeleteTimeExpenseRow(expenseEntry.key, e)
                                                        }
                                                        className={classes.editIcon}
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null}
                                        </Box>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                        {totalMileageAmount > 0 && (
                            <TableRow>
                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }}>
                                    <Box className={classes.mileageTitle} style={{ paddingTop: 20 }}>{`Mileage`}</Box>
                                </TableCell>

                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }}>
                                    <Box className={classes.totalAmount}>
                                        <Box> {displayCurrencyAmount(totalMileageAmount)}</Box>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                        <TableRow>
                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }}>
                                <Box className={classes.totalAmount}>
                                    <Box className={classes.totalTitle}>{'Total'}</Box>
                                </Box>
                            </TableCell>

                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }}>
                                <Box className={classes.totalAmount}>
                                    <Box style={{ paddingTop: 0 }}>
                                        {' '}
                                        {displayCurrencyAmount(totalAmount + totalMileageAmount)}
                                    </Box>
                                </Box>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default TimeExpenseEntry;
